import React from 'react'
import { fmtDateTime } from '../utils/datetime'
import { PROVIDER_NOT_RECORDED, providerLabel } from '../utils/methods'
import { theme } from '../theme'
import { Hint } from './Hint'
import {
  DASH,
  describeTimestamp,
  fmtCount,
  fmtDuration,
  inferInboundTransport,
  airtimePerPage,
  internalPhases,
  intervalSeconds,
  milestones,
  namedIntervals,
  outboundSecondSource,
  podLine,
  timelineSegments,
  timestampLabel,
  tsMillis,
  MILESTONE_ORDER,
  type TransmissionMeasurementSource,
} from './transmissionTiming'

export type { TransmissionMeasurementSource } from './transmissionTiming'

/**
 * The measurements this project has always CAPTURED but never DISPLAYED: the four
 * inbound clocks, the pod attribution for both legs, the provider's own transmission
 * airtime, and the page count actually put on the wire.
 *
 * EVERY CAPTION AND HINT NAMING A VENDOR IS RESOLVED FROM THE ROW. The panel shipped with
 * "eFax" spelled into eight of them over columns all three providers write, so an SRFax
 * transmission was described in eFax's words — a misattributed measurement, not a cosmetic
 * slip. `providerLabel` answers the name and a NULL provider reads as NOT RECORDED, never as
 * a default brand.
 *
 * Shared by RunDetail and BatchItemDetail on purpose. Two hand-written copies of a
 * per-leg display drifted apart once already (see the header of SlaCell.tsx), and the
 * failure mode was not a crash — it was two pages quietly disagreeing about the same
 * transmission. One component means they cannot.
 *
 * EVERY FIELD IS TREATED AS NULL AT RUNTIME. These fields are declared on the API type
 * but the endpoints do not populate them yet, so until that half ships they arrive as
 * JSON null for every row. Nothing here may render NaN, "undefined", "Infinity", or a
 * fabricated 0 — an absent measurement is an em dash, and a section with nothing in it
 * says so in words rather than presenting an empty grid that reads as "all zero". The
 * arithmetic all lives in ./transmissionTiming so a test can execute it against an
 * all-null row instead of inspecting the shape of a guard clause.
 *
 * The three distinct kinds of blank, which must not be conflated:
 *
 *   MISSING      the field could have had a value and does not — em dash plus "not
 *                captured for this transmission".
 *   UNOBTAINABLE the field can NEVER have a value on this transport. The AFC (inbound)
 *                pod is the case that matters: it is read out of the inbound email
 *                Message-ID, and an eAPI webhook payload has no equivalent field —
 *                proven against the vendored eFax OpenAPI spec. A blank AFC pod on a
 *                webhook run is CORRECT and PERMANENT, so it is labelled as not
 *                available on that transport rather than shown as missing or as an error.
 *   UNKNOWABLE   we cannot even tell which transport carried the inbound leg, so we
 *                cannot say which of the two above applies. Said plainly.
 */

const GREY = theme.textMuted

// ------------------------------------------------------------- naming the leg's provider
//
// WHY THESE EXIST. This panel began as eFax-only and spelled "eFax" into eight captions and
// hints over columns EVERY provider writes: `t_efax_completed` / `t_efax_notified` are written
// from an SFax webhook payload as readily as from eFax's, and `fax_duration_seconds` is written
// from SFax's confirmation email by `services/second_source.py`. A caption reading "eFax" over
// an SRFax transmission does not misspell a brand — it attributes a measurement to a vendor
// that never carried it, which is the one error this application exists not to make.
//
// A NULL provider is NOT RECORDED and is never resolved to a default. These columns are
// nullable with no server default and are deliberately not backfilled, so every historical row
// has NULL; naming eFax over one would invent a measurement rather than report an absence.

/** The brand for a leg whose provider was RECORDED, or `null` when it was not. */
function recordedProvider(provider: string | null | undefined): string | null {
  const p = (provider ?? '').trim()
  return p ? providerLabel(p) : null
}

/**
 * The brand to name for a measurement EITHER LEG can have written, or `null` for "say nothing".
 *
 * `t_efax_completed` arrives from the inbound payload on a webhook row and from the OUTBOUND
 * send-notification headers on an SMTP one (`services/receive_capture.py` writes it from both),
 * and the same is true of `fax_duration_seconds`. So on a MIXED-PROVIDER row — supported, and
 * the job editor says so in as many words — naming either leg's brand would be a guess. This
 * answers a brand only when both legs are recorded AND agree; anything else gets the neutral
 * wording, which states an absence instead of resolving it.
 */
function agreedProvider(source: TransmissionMeasurementSource): string | null {
  const out = (source.outbound_provider ?? '').trim().toLowerCase()
  const inb = (source.inbound_provider ?? '').trim().toLowerCase()
  if (!out || !inb || out !== inb) return null
  return providerLabel(out)
}

/** The subject of a sentence about a provider: its brand, or a neutral stand-in. */
function providerSubject(brand: string | null): string {
  return brand ?? 'the provider'
}

/** Said out loud when no brand could be named, so the gap is stated rather than implied. */
function notRecordedNote(brand: string | null): string {
  return brand
    ? ''
    : ` The provider that produced this value is ${PROVIDER_NOT_RECORDED} on this transmission.`
}

// -------------------------------------------------------------------------- pieces

function Grid({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'auto 1fr',
        gap: '0.3rem 0.9rem',
        fontSize: '0.8rem',
        alignItems: 'baseline',
      }}
    >
      {children}
    </div>
  )
}

/* The private `Field` that used to live here is GONE. It was a hover-only copy of the
 * `title=` + `cursor: 'help'` convention, and this file adding a fourth copy of that
 * affordance is what G5.16 forbids. Every field below renders through the exported
 * `Hint` (./Hint.tsx), which keeps the same convention and adds click / tap and
 * keyboard activation with `aria-describedby`. The props are unchanged, so the call
 * sites read the same — what changed is that there is now one implementation of the
 * behaviour instead of one per surface. */

/** An em dash with the reason it is blank. Never an error style — a blank measurement is
 *  not a fault, and colouring it like one sends operators hunting a bug that isn't there. */
function Blank({ why }: { why: string }) {
  return (
    <span style={{ color: GREY }}>
      {DASH} <span style={{ fontStyle: 'italic', fontSize: '0.76rem' }}>{why}</span>
    </span>
  )
}

/** A sentence explaining why a whole section is empty. */
function NotCaptured({ children }: { children: React.ReactNode }) {
  return <p style={{ margin: 0, fontSize: '0.8rem', color: GREY, lineHeight: 1.5 }}>{children}</p>
}

function SubHeading({ children }: { children: React.ReactNode }) {
  return (
    <h4
      style={{
        margin: '0 0 0.45rem',
        fontSize: '0.78rem',
        fontWeight: 600,
        color: theme.text,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
      }}
    >
      {children}
    </h4>
  )
}

function Block({ children }: { children: React.ReactNode }) {
  return <div style={{ marginBottom: '1.1rem' }}>{children}</div>
}

// ------------------------------------------------------------------------ timeline UI

/**
 * The relative-timing visual: one proportional bar plus a legend of the same segments.
 *
 * The bar is the point of this whole panel — an operator should SEE where the time went
 * instead of subtracting six timestamps in their head. The legend repeats every segment
 * numerically because a sub-second segment is a hairline on the bar and would otherwise
 * be unreadable, and because the bar applies a minimum width so a tiny-but-real segment
 * stays visible; the legend is where the true number always lives.
 */
export function TransmissionTimeline({ source }: { source: TransmissionMeasurementSource }) {
  const points = milestones(source)
  const segments = timelineSegments(source)

  if (segments.length === 0) {
    return (
      <div>
        <NotCaptured>
          {points.length === 1 ? (
            <>
              Only one clock was captured for this transmission (
              <strong style={{ color: theme.text }}>{points[0].label}</strong> at{' '}
              {fmtDateTime(points[0].at)}), so there is no interval to lay out. A timing
              breakdown needs at least two.
            </>
          ) : (
            <>
              No timing clocks were captured for this transmission, so there is nothing to lay
              out.
            </>
          )}
        </NotCaptured>
        <OurOwnPipeline source={source} />
        <OutboundSecondSource source={source} />
      </div>
    )
  }

  const totalSeconds = intervalSeconds(points[0].at, points[points.length - 1].at)
  const missingClocks = MILESTONE_ORDER.length - points.length

  return (
    <div>
      <div
        style={{
          display: 'flex',
          width: '100%',
          height: '1.5rem',
          borderRadius: 4,
          overflow: 'hidden',
          border: `1px solid ${theme.border}`,
          background: theme.bg,
        }}
      >
        {segments.map(seg => (
          <div
            key={`${seg.fromKey}->${seg.toKey}`}
            title={`${seg.label}: ${fmtDuration(seg.seconds)}`}
            style={{
              // A segment with a real but tiny duration must still be visible, hence the
              // floor. It is presentational only; the legend below carries the true value.
              flexGrow: Math.max(seg.fraction, 0.004),
              flexBasis: 0,
              minWidth: '2px',
              background: seg.color,
            }}
          />
        ))}
      </div>

      <div style={{ marginTop: '0.55rem', display: 'grid', gap: '0.2rem', fontSize: '0.76rem' }}>
        {segments.map(seg => (
          <div
            key={`legend-${seg.fromKey}->${seg.toKey}`}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <span
              style={{
                width: '0.7rem', height: '0.7rem', borderRadius: 2,
                background: seg.color, flexShrink: 0,
              }}
            />
            <span style={{ color: theme.text }}>{seg.label}</span>
            <span style={{ color: GREY, marginLeft: 'auto', fontVariantNumeric: 'tabular-nums' }}>
              {fmtDuration(seg.seconds)}
              {seg.seconds < 0 ? ' (clocks out of order)' : ''}
            </span>
          </div>
        ))}
        <div
          style={{
            display: 'flex', gap: '0.5rem', marginTop: '0.25rem',
            paddingTop: '0.3rem', borderTop: `1px solid ${theme.border}`,
          }}
        >
          <span style={{ color: GREY }}>Total span, first captured clock to last</span>
          <span
            style={{ color: theme.text, marginLeft: 'auto', fontVariantNumeric: 'tabular-nums' }}
          >
            {fmtDuration(totalSeconds)}
          </span>
        </div>
      </div>

      {missingClocks > 0 && (
        <p style={{ margin: '0.55rem 0 0', fontSize: '0.74rem', color: GREY }}>
          {missingClocks} of the {MILESTONE_ORDER.length} clocks were not captured for this
          transmission, so the breakdown above covers only the intervals that could be
          measured.
        </p>
      )}

      <OurOwnPipeline source={source} />
      <OutboundSecondSource source={source} />
    </div>
  )
}

/**
 * OUR OWN pipeline cost, in its own block. G10.4.
 *
 * The separation is structural, not cosmetic. An internal phase is never a member of
 * `MILESTONE_ORDER`, so no adjacent-milestone pairing in `timelineSegments` can fold our
 * cost into a provider leg's segment — the one thing G10.4 forbids. It appears in the
 * timeline (here, immediately under the bar) but it is never a slice of the bar, and it is
 * stated in words that it cannot move an SLA verdict.
 *
 * An absent phase renders as an EM DASH with the reason, never as zero. A missing
 * `t_outbound_request_started` means the value was not recorded — on every row written
 * before this measurement landed — and rendering that as "0 ms" would claim the provider
 * accepted our send instantly.
 */
export function OurOwnPipeline({ source }: { source: TransmissionMeasurementSource }) {
  const phases = internalPhases(source)

  return (
    <div
      style={{
        marginTop: '0.8rem',
        paddingTop: '0.6rem',
        borderTop: `1px dashed ${theme.border}`,
      }}
    >
      <div
        style={{
          fontSize: '0.72rem',
          color: GREY,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          marginBottom: '0.35rem',
        }}
      >
        Our own pipeline — not part of any leg
      </div>
      <Grid>
        {phases.length > 0 ? (
          phases.map(phase => (
            <Hint
              key={phase.key}
              label={phase.label}
              value={fmtDuration(phase.seconds)}
              hint={phase.hint}
            />
          ))
        ) : (
          <Hint
            label="Our POST latency (time to accept)"
            value={
              <Blank
                why={
                  'not recorded for this transmission — an absent request-start is NOT zero ' +
                  'latency'
                }
              />
            }
            hint={
              't_submitted − t_outbound_request_started: how long the provider took to accept ' +
              'our send. OUR OWN pipeline cost. This row has no recorded request start, so ' +
              'the latency cannot be derived — which is not the same as it having been zero.'
            }
            muted
          />
        )}
      </Grid>
      <p style={{ margin: '0.45rem 0 0', fontSize: '0.72rem', color: GREY, lineHeight: 1.45 }}>
        Measured with our own clock at the call site. The outbound SLA starts when the
        provider accepts, so nothing here enters a verdict — it is reported separately so our
        cost is visible rather than charged to the platform.
      </p>
    </div>
  )
}

// -------------------------------------------------------------------- inbound clocks

/**
 * The four inbound clocks, each named for the system whose clock it is, plus the two
 * intervals worth naming and our own observation delay.
 */
export function InboundClocks({ source }: { source: TransmissionMeasurementSource }) {
  const { efaxNotifyLagSeconds, networkQueueSeconds, observationDelaySeconds } =
    namedIntervals(source)

  // The two provider clocks and the lag between them are named from the row's OWN provider,
  // never from a literal — and from `agreedProvider`, because either leg can have written them.
  const brand = agreedProvider(source)
  const who = providerSubject(brand)
  const gap = notRecordedNote(brand)

  const anyClock =
    tsMillis(source.t_efax_completed) !== null ||
    tsMillis(source.t_efax_notified) !== null ||
    tsMillis(source.t_notified) !== null ||
    tsMillis(source.t_received) !== null

  if (!anyClock) {
    return (
      <NotCaptured>
        None of the four inbound clocks were captured for this transmission. They are recorded
        per transmission and are not backfilled, so a transmission that ran before this
        measurement landed has none.
      </NotCaptured>
    )
  }

  return (
    <>
      <Grid>
        <Hint
          label={timestampLabel('t_efax_completed')}
          value={fmtDateTime(source.t_efax_completed)}
          hint={`${who}'s own clock: when ${who} says the fax finished.${gap}`}
          muted={!source.t_efax_completed}
        />
        <Hint
          label={timestampLabel('t_efax_notified')}
          value={fmtDateTime(source.t_efax_notified)}
          hint={`${who}'s own clock: when ${who} emitted the notification.${gap}`}
          muted={!source.t_efax_notified}
        />
        <Hint
          label="Notification landed"
          value={fmtDateTime(source.t_notified)}
          hint="Our clock: when the notification actually reached us."
          muted={!source.t_notified}
        />
        <Hint
          label="Delivery (best estimate)"
          value={fmtDateTime(source.t_received)}
          hint="Best estimate of actual delivery, deliberately NOT normalised to our polling clock."
          muted={!source.t_received}
        />
      </Grid>

      <div
        style={{ marginTop: '0.6rem', paddingTop: '0.5rem', borderTop: `1px solid ${theme.border}` }}
      >
        <Grid>
          <Hint
            label={`${brand ?? 'Provider'} notify lag`}
            value={fmtDuration(efaxNotifyLagSeconds)}
            hint={
              `t_efax_notified − t_efax_completed: time inside ${who} between finishing the ` +
              `fax and telling anyone about it.${gap}`
            }
            muted={efaxNotifyLagSeconds === null}
          />
          <Hint
            label="Network + queue"
            value={fmtDuration(networkQueueSeconds)}
            hint={
              `t_notified − t_efax_notified: time between ${who} emitting the notification ` +
              `and it landing on us.${gap}`
            }
            muted={networkQueueSeconds === null}
          />
          <Hint
            label="Our observation delay"
            value={fmtDuration(observationDelaySeconds)}
            hint="t_notified − t_received: how much later we LEARNED of delivery than delivery is estimated to have happened. Not charged to the SLA."
            muted={observationDelaySeconds === null}
          />
        </Grid>
      </div>
    </>
  )
}

// ---------------------------------------------------------------------- pod attribution

/**
 * CCS pod (outbound) and AFC pod (inbound) — the names an eFax operator uses.
 *
 * The AFC pod is the one case where blank is a FINAL ANSWER rather than a gap. It is
 * parsed out of the inbound email Message-ID, so an eAPI-webhook-inbound transmission
 * cannot carry one: the webhook payload has no equivalent field at all. That is stated on
 * screen in those terms, because a permanently blank cell with no explanation is
 * indistinguishable from a bug and gets re-reported as one every few months.
 */
export function PodAttribution({ source }: { source: TransmissionMeasurementSource }) {
  const transport = inferInboundTransport(source)
  const ccs = podLine(source.outbound_pod_name, source.outbound_pod_region, source.outbound_pod_env)
  const afc = podLine(source.inbound_pod_name, source.inbound_pod_region, source.inbound_pod_env)
  // PER LEG here, unlike the clocks above: a pod is parsed from that leg's own envelope, so the
  // outbound pod belongs to the outbound provider and the inbound pod to the inbound one. A
  // mixed-provider job is supported, so one brand for both legs would be wrong by construction.
  const outboundBrand = recordedProvider(source.outbound_provider)
  const inboundBrand = recordedProvider(source.inbound_provider)

  let afcValue: React.ReactNode
  if (afc) {
    afcValue = afc
  } else if (transport === 'eapi_webhook') {
    afcValue = (
      <span style={{ color: GREY, fontStyle: 'italic' }}>
        Not available on this transport. The AFC pod is read out of the inbound email
        Message-ID, and a webhook payload has no equivalent field — so this stays blank for
        every webhook-inbound transmission{inboundBrand ? ` (this one arrived over ${inboundBrand})` : ''}.
        Nothing is missing.
      </span>
    )
  } else if (transport === 'imap') {
    afcValue = (
      <Blank
        why={
          'not captured for this transmission — IMAP inbound can carry a pod, so this one ' +
          'either predates the measurement or arrived without a parseable Message-ID'
        }
      />
    )
  } else {
    afcValue = (
      <Blank
        why={
          'inbound transport unknown for this transmission, so we cannot say whether a pod ' +
          'was obtainable at all'
        }
      />
    )
  }

  return (
    <Grid>
      <Hint
        label="CCS pod (outbound)"
        value={ccs ?? <Blank why="not captured for this transmission" />}
        hint={
          `The pod that handled the OUTBOUND leg, parsed from the J2MsgID header. Only a ` +
          `provider that stamps a J2MsgID carries one; this leg was carried by ` +
          `${outboundBrand ?? PROVIDER_NOT_RECORDED}.`
        }
        muted={!ccs}
      />
      {source.outbound_msg_id ? (
        <Hint
          label="Outbound J2MsgID"
          value={<code style={{ fontSize: '0.74rem' }}>{source.outbound_msg_id}</code>}
          hint="The raw J2MsgID the outbound pod name was parsed from. NOT the SMTP message id — that is issued by a different system in a different format."
        />
      ) : null}
      <Hint
        label="AFC pod (inbound)"
        value={afcValue}
        hint={
          `The pod that handled the INBOUND leg. Only obtainable from an inbound email ` +
          `Message-ID, so webhook inbound never has one; this leg was carried by ` +
          `${inboundBrand ?? PROVIDER_NOT_RECORDED}.`
        }
        muted={!afc}
      />
      {source.inbound_msg_id ? (
        <Hint
          label="Inbound J2MsgID"
          value={<code style={{ fontSize: '0.74rem' }}>{source.inbound_msg_id}</code>}
          hint="The raw J2MsgID the inbound pod name was parsed from."
        />
      ) : null}
    </Grid>
  )
}

// --------------------------------------------- W5: the outbound leg's second source

/**
 * The outbound completion the provider's OWN reported duration implies, beside the scored one.
 *
 * WHY THIS BLOCK EXISTS AT ALL. On SFax, `t_fax_sent` is `faxdateiso`, which equals the
 * confirmation email's `Time` to the second — and that is SFax's ACCEPTANCE, not its completion.
 * One real run submitted at 15:42:51 UTC had `faxdateiso` of 15:43:02, eleven seconds later, with
 * a reported duration of 51 s: the transmission actually finished around 15:43:53, so the leg
 * reads 11 s for about 62 s and the bias FLATTERS the provider.
 *
 * The derived value is a SECOND SOURCE and does NOT score (Decision 1, settled 2026-09-12), and
 * the operator chose that over correcting the scored number precisely so the gap would be VISIBLE
 * on every such fax instead of one value being silently swapped for another. So this block's job
 * is to show BOTH readings and the difference between them, and to say in words which one a
 * verdict uses. A derived number displayed without the scored one beside it would be the
 * correction the decision rejected, wearing a different label.
 *
 * ABSENT ENTIRELY when no second source was derived, rather than rendering an em-dash row. Unlike
 * an absent POST latency — which is a measurement we should have and do not — a missing derived
 * completion is the NORMAL state for eFax and for every provider that reports a real completion
 * time, so a permanent blank row here would read as a fault on the providers that have nothing
 * wrong with them.
 */
export function OutboundSecondSource({ source }: { source: TransmissionMeasurementSource }) {
  const reading = outboundSecondSource(source)
  if (!reading) return null

  const provenance = source.timestamp_provenance?.['t_fax_sent_derived']
  const derivation = describeTimestamp('t_fax_sent_derived', source)

  return (
    <div
      style={{
        marginTop: '0.8rem',
        paddingTop: '0.6rem',
        borderTop: `1px dashed ${theme.border}`,
      }}
    >
      <div
        style={{
          fontSize: '0.72rem',
          color: GREY,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          marginBottom: '0.35rem',
        }}
      >
        Second source — recorded and shown, never scored
      </div>
      <Grid>
        <Hint
          label="Outbound leg (scored)"
          value={fmtDuration(reading.scoredSeconds)}
          hint={
            't_fax_sent − t_submitted. THIS is the number the outbound SLA verdict uses. On a ' +
            "provider whose reported completion is really its acceptance, it is short by the " +
            'transmission time.'
          }
          muted={reading.scoredSeconds === null}
        />
        <Hint
          label="Outbound leg (derived)"
          value={fmtDuration(reading.derivedSeconds)}
          hint={
            't_fax_sent_derived − t_submitted: the leg the provider\u2019s own reported ' +
            'duration implies. Recorded and shown for comparison; it does not enter any SLA ' +
            'verdict.'
          }
          muted={reading.derivedSeconds === null}
        />
        <Hint
          label="Difference"
          value={fmtDuration(reading.understatedBySeconds)}
          hint={
            'How much longer the derived reading is than the scored one. A negative value means ' +
            'the derived completion precedes the scored one, which cannot happen if the scored ' +
            'value is an acceptance time \u2014 so it says the derivation is wrong on this row.'
          }
          muted={reading.understatedBySeconds === null}
        />
        <Hint
          label={timestampLabel('t_fax_sent_derived')}
          value={fmtDateTime(reading.at)}
          hint={derivation.text}
        />
      </Grid>
      <p style={{ margin: '0.45rem 0 0', fontSize: '0.72rem', color: GREY, lineHeight: 1.45 }}>
        The scored value stays the outbound leg&rsquo;s. This second reading is
        {provenance?.code ? ` \u0060${provenance.code}\u0060 \u2014 ` : ' '}
        our arithmetic on two fields the provider reported separately, kept beside the scored one
        so the gap between them is visible on every fax rather than judged from a single run.
      </p>
    </div>
  )
}

// ------------------------------------------------------------------- airtime + pages

/**
 * The provider's own transmission airtime and the pages actually put on the wire.
 *
 * `sent_page_count` is per transmission on purpose: the job's document is mutable, so
 * reading the page count off the job would silently rewrite the history of every past
 * transmission each time someone replaces a document.
 */
export function TransmissionFacts({ source }: { source: TransmissionMeasurementSource }) {
  const duration = source.fax_duration_seconds
  const pages = source.sent_page_count
  const haveDuration = typeof duration === 'number' && Number.isFinite(duration)
  const havePages = typeof pages === 'number' && Number.isFinite(pages)
  // `fax_duration_seconds` is written from eFax's X-J2Fax* headers, from an eAPI metadata
  // response, from an SFax confirmation email (services/second_source.py) and from an inbound
  // payload — so the brand comes from the row and only when both legs agree on one.
  const brand = agreedProvider(source)
  const who = providerSubject(brand)
  const gap = notRecordedNote(brand)

  if (!haveDuration && !havePages) {
    return (
      <NotCaptured>
        Neither the transmission airtime nor the sent page count was captured for this
        transmission.
      </NotCaptured>
    )
  }

  const perPage = airtimePerPage(duration, pages)

  return (
    <Grid>
      <Hint
        label={brand ? `Fax airtime (${brand})` : 'Fax airtime'}
        value={fmtDuration(haveDuration ? duration : null)}
        hint={
          `Transmission duration as ${who} itself reports it — time on the wire, not our ` +
          `end-to-end measurement.${gap}`
        }
        muted={!haveDuration}
      />
      <Hint
        label="Pages sent"
        value={fmtCount(havePages ? pages : null)}
        hint="Pages actually transmitted for THIS run. Not the job's current document page count, which can change."
        muted={!havePages}
      />
      <Hint
        label="Airtime per page"
        value={fmtDuration(perPage)}
        hint="Airtime divided by pages sent. Shown only when both were captured and at least one page was sent."
        muted={perPage === null}
      />
    </Grid>
  )
}

// ------------------------------------------------------------------------- container

/**
 * The whole panel. Both views render exactly this, so neither can show a different set of
 * measurements — or a different explanation of a blank — than the other.
 */
export function TransmissionMeasurements({ source }: { source: TransmissionMeasurementSource }) {
  return (
    <div>
      <Block>
        <SubHeading>Where the time went</SubHeading>
        <TransmissionTimeline source={source} />
      </Block>
      <Block>
        <SubHeading>Inbound clocks</SubHeading>
        <InboundClocks source={source} />
      </Block>
      <Block>
        <SubHeading>Pod attribution</SubHeading>
        <PodAttribution source={source} />
      </Block>
      <div>
        <SubHeading>Airtime &amp; pages</SubHeading>
        <TransmissionFacts source={source} />
      </div>
    </div>
  )
}
